/**
 * profileContract.js — Single source of truth for directory profile field names,
 * enum vocabularies, and shared constants. Import from here; never hardcode field
 * names or enum strings in page/component files.
 *
 * Backend contract: FIELD values must be a subset of DIRECTORY_ALLOWED_FIELDS in
 * backend/server.py. The smoke test (backend/test_field_contract.py) asserts this.
 */

// ── Canonical wire field names ────────────────────────────────────────────────
// One entry per "drifting" field. Keys are semantic aliases used in JS code;
// values are the canonical strings stored & returned by the backend.
export const FIELD = {
  gigCount:          'stage8GigCount',
  rating:            'averageRating',
  rebookRate:        'rebookRate',
  specialties:       'specialties',
  hasOwnEquipment:   'hasOwnEquipment',
  eventTypes:        'eventTypes',
  baseRate:          'baseRate',
  availabilityStatus:'availabilityStatus',
};

// ── Rate-type vocabulary (lowercase — matches OnboardingRateType enum) ────────
// ONE canonical vocabulary. The wizard writes these lowercase values.
export const RATE_TYPES = {
  musician: [
    { value: 'flat',        label: 'Flat rate',    desc: 'Fixed price per gig' },
    { value: 'hourly',      label: 'Hourly',       desc: 'Per hour worked' },
    { value: 'part_of_day', label: 'Part of day',  desc: 'Morning/afternoon/evening' },
  ],
  tech: [
    { value: 'hourly',      label: 'Hourly',       desc: 'Per hour worked' },
    { value: 'part_of_day', label: 'Part of day',  desc: 'Morning/afternoon/evening' },
    { value: 'flat',        label: 'Flat rate',    desc: 'Fixed price per gig' },
  ],
};

/**
 * Return a human label for a rate-type value.
 * Handles all three historical vocabularies (lowercase wizard, UPPERCASE RateType,
 * legacy FIXED/PER_GIG) so existing data renders correctly.
 */
export function rateTypeLabel(v) {
  switch (v) {
    case 'flat':
    case 'FLAT_EVENT':
    case 'FIXED':
      return 'Flat Rate';
    case 'hourly':
    case 'HOURLY':
      return 'Per Hour';
    case 'part_of_day':
    case 'PART_OF_DAY':
      return 'Part of Day';
    case 'PER_GIG':
      return 'Per Gig';
    default:
      return v || 'Per Gig';
  }
}

// ── Experience-level vocabulary ───────────────────────────────────────────────
export const EXPERIENCE = {
  musician: [
    { value: 'beginner',     label: 'Beginner',              desc: '1-3 years experience' },
    { value: 'intermediate', label: 'Intermediate',          desc: '3-7 years experience' },
    { value: 'experienced',  label: 'Experienced',           desc: '7-12 years experience' },
    { value: 'senior',       label: 'Senior',                desc: '12+ years experience' },
    { value: 'touring_pro',  label: 'Touring professional',  desc: 'Full-time touring' },
  ],
  tech: [
    { value: 'junior',      label: 'Junior',               desc: '1-3 years experience' },
    { value: 'medior',      label: 'Medior',               desc: '3-7 years experience' },
    { value: 'senior',      label: 'Senior',               desc: '7+ years experience' },
    { value: 'touring_pro', label: 'Touring professional', desc: 'Full-time touring' },
  ],
};

/**
 * Return a human label for an experience level value.
 * Handles old UPPERCASE enum values (JUNIOR/MEDIOR/SENIOR_SPECIALIST) and new
 * lowercase wizard values.
 */
export function experienceLabel(v, profileType = 'musician') {
  // Legacy UPPERCASE fallbacks
  switch (v) {
    case 'JUNIOR':           return 'Junior';
    case 'MEDIOR':           return 'Medior';
    case 'SENIOR_SPECIALIST':return 'Senior / Specialist';
    default: break;
  }
  const levels = EXPERIENCE[profileType] || EXPERIENCE.musician;
  const found = levels.find((l) => l.value === v);
  return found ? found.label : (v || '');
}

// ── Sort options (deduplicated from MusiciansPage + TechPage) ─────────────────
export const SORT_OPTIONS = [
  { key: null,           label: 'Best match' },
  { key: 'rate_low',     label: 'Lowest rate' },
  { key: 'rate_high',    label: 'Highest rate' },
  { key: 'rating',       label: 'Top rated' },
  { key: 'most_rebooked',label: 'Most rebooked' },
];

// ── Rate buckets (page-specific; different thresholds per type) ───────────────
export const RATE_BUCKETS = {
  musician: [
    { label: '<€250',     max: 250 },
    { label: '€250–500',  min: 250, max: 500 },
    { label: '€500+',     min: 500 },
  ],
  tech: [
    { label: '<€200',     max: 200 },
    { label: '€200–400',  min: 200, max: 400 },
    { label: '€400+',     min: 400 },
  ],
};

// ── Inline lists (moved from page-level constants) ────────────────────────────
export const INSTRUMENTS = ['Vocals', 'Guitar', 'Drums', 'Keys', 'DJ', 'Bass', 'Saxophone', 'Violin'];

export const ROLES = ['FOH', 'Monitor', 'Lighting', 'Stage', 'AV', 'Backline', 'Video', 'Rigging'];

// ── venueName accessor ────────────────────────────────────────────────────────
/**
 * Robust venueName accessor that handles both nested (request.venue.venueName)
 * and flat (request.venueName) shapes. Always prefer the nested shape; fall back
 * to flat only for denormalized snapshots.
 */
export function readVenueName(request) {
  return request?.venue?.venueName ?? request?.venueName ?? '';
}
